import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  TablePagination,
  IconButton,
} from '@material-ui/core';
import {
  fetchAppraisalNoticeProperties,
  fetchAppraisalNoticeTaxingUnits,
  fetchAppraisalNoticeExemptions,
} from '@modules/appraisalAdmin/actions/notice';
import { useParams, useHistory, Link as RouterLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@material-ui/icons';
import PropTypes from 'prop-types';

const TABLE_OPTIONS = {
  PROPERTY: 'Property',
  TAXING_UNIT: 'Property Taxing Unit',
  EXEMPTIONS: 'Property Taxing Unit Exemption',
};

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 67px)', // minus header height
    overflow: 'hidden',
  },
  header: {
    padding: '24px 24px 0 24px',
    flexShrink: 0, // Prevent header from shrinking
  },
  tableContainer: {
    flex: 1,
    margin: '16px 24px 0 24px',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0, // Important for Firefox
  },
  tableWrapper: {
    flex: 1,
    position: 'relative', // For proper table height calculation
    overflow: 'auto',
  },
  table: {
    tableLayout: 'fixed', // Prevent horizontal scroll
    width: '100%',
  },
  headerCell: {
    padding: '8px 16px',
    fontWeight: 'bold',
    fontSize: '14px',
    color: 'rgba(0, 0, 0, 0.87)',
    backgroundColor: '#f5f5f5',
  },
  tableCell: {
    padding: '8px 16px',
    fontSize: '14px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  paginationWrapper: {
    flexShrink: 0, // Prevent pagination from shrinking
    backgroundColor: '#fff',
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    padding: 0,
  },
};

const PropertyDetailsView = () => {
  const { noticeId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const [data, setData] = useState({ results: [], countResults: [] });
  const [selectedTable, setSelectedTable] = useState(TABLE_OPTIONS.PROPERTY);
  const [searchPid, setSearchPid] = useState('');
  const [activeSearchPid, setActiveSearchPid] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    if (noticeId) {
      loadData();
    }
  }, [page, rowsPerPage, selectedTable, activeSearchPid]);

  const loadData = async () => {
    if (!noticeId) {
      console.error('No noticeId available');
      return;
    }

    setLoading(true);
    try {
      let apiCall;
      const params = {
        noticeID: Number.parseInt(noticeId, 10),
        page: page,
        pageSize: rowsPerPage,
      };

      if (activeSearchPid) {
        params.pID = Number.parseInt(activeSearchPid, 10);
      }

      switch (selectedTable) {
        case TABLE_OPTIONS.PROPERTY:
          apiCall = fetchAppraisalNoticeProperties;
          break;
        case TABLE_OPTIONS.TAXING_UNIT:
          apiCall = fetchAppraisalNoticeTaxingUnits;
          break;
        case TABLE_OPTIONS.EXEMPTIONS:
          apiCall = fetchAppraisalNoticeExemptions;
          break;
        default:
          apiCall = fetchAppraisalNoticeProperties;
      }

      const result = await dispatch(apiCall(params));

      if (result?.payload?.results || result?.results) {
        const results = result?.payload?.results || result?.results;
        setData({
          results,
          countResults: result?.payload?.countResults || result?.countResults,
        });
        setTotalCount(
          (result?.payload?.countResults || result?.countResults)?.[0]?.count ||
            0,
        );

        // Generate columns from the first result
        if (results?.[0]) {
          const allKeys = Object.keys(results[0]);

          // Create dynamic columns with pID first if it exists
          const dynamicColumns = [];

          // Add pID first if it exists
          if (allKeys.includes('pID')) {
            dynamicColumns.push({
              id: 'pID',
              label: 'pID',
              width: 200,
            });
          }

          // Add all other columns
          for (const key of allKeys) {
            if (key !== 'pID') {
              dynamicColumns.push({
                id: key,
                label: key,
                width: 200,
              });
            }
          }

          setColumns(dynamicColumns);
        }
      }
    } catch (error) {
      console.error('Error loading property details:', error);
    }
    setLoading(false);
  };

  const handleSearch = () => {
    if (searchPid) {
      setPage(0); // Reset to first page when searching
      setActiveSearchPid(searchPid); // Set the active search PID
    }
  };

  const handleClearSearch = () => {
    setSearchPid(''); // Clear the search input
    setActiveSearchPid(''); // Clear the active search
    setPage(0); // Reset to first page
  };

  const getColumns = () => columns;

  const handleBack = () => {
    history.push('/appraisal-admin/appraisal-notices');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFirstPageButtonClick = () => {
    setPage(0);
  };

  const handleLastPageButtonClick = () => {
    setPage(Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1));
  };

  const TablePaginationActions = (props) => {
    const { count, page, rowsPerPage, onPageChange } = props;
    const currentPage = page + 1;
    const totalPages = Math.ceil(count / rowsPerPage);

    return (
      <Box display="flex" alignItems="center">
        <IconButton
          onClick={handleFirstPageButtonClick}
          disabled={page === 0}
          aria-label="first page"
        >
          <FirstPageIcon />
        </IconButton>
        <IconButton
          onClick={(event) => onPageChange(event, page - 1)}
          disabled={page === 0}
          aria-label="previous page"
        >
          <KeyboardArrowLeft />
        </IconButton>

        <Typography
          variant="body2"
          style={{
            margin: '0 16px',
            whiteSpace: 'nowrap',
            display: 'inline-block',
          }}
        >
          Page {currentPage} of {totalPages}
        </Typography>

        <IconButton
          onClick={(event) => onPageChange(event, page + 1)}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          <LastPageIcon />
        </IconButton>
      </Box>
    );
  };

  TablePaginationActions.propTypes = {
    count: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
  };

  const renderPidLink = (pid, row) => (
    <Link
      to={`/appraisal/property/${pid}/${row.pYear}/${row.pVersion}/${row.pRollCorr}`}
      component={RouterLink}
      style={{ textDecoration: 'underline', color: 'blue' }}
      target="_blank"
    >
      {pid}
    </Link>
  );

  const handleTableChange = (event) => {
    setSelectedTable(event.target.value);
    setPage(0); // Reset to first page when changing tables
  };

  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<Typography color="textSecondary">&gt;</Typography>}
        >
          <Link
            color="inherit"
            onClick={handleBack}
            style={{ cursor: 'pointer' }}
          >
            Appraisal Notice
          </Link>
          <Link
            color="inherit"
            onClick={handleBack}
            style={{ cursor: 'pointer' }}
          >
            {noticeId}
          </Link>
          <Typography color="textPrimary">Property Details</Typography>
        </Breadcrumbs>

        <Box mt={3} mb={2}>
          <Box display="flex" alignItems="center" mb={2}>
            <Typography style={{ marginRight: 16 }}>Table:</Typography>
            <Select
              value={selectedTable}
              onChange={handleTableChange}
              style={{ minWidth: 200, marginRight: 16 }}
            >
              {Object.values(TABLE_OPTIONS).map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <Typography style={{ marginRight: 16, marginLeft: 16 }}>
              Search by PID:
            </Typography>
            <TextField
              value={searchPid}
              onChange={(e) => setSearchPid(e.target.value)}
              style={{ marginRight: 16 }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && searchPid.trim()) {
                  handleSearch();
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={!searchPid.trim()} // Disable if searchPid is empty or only whitespace
              style={{ marginRight: 8 }}
            >
              Search
            </Button>
            <Button
              variant="text"
              onClick={handleClearSearch}
              disabled={!searchPid && !activeSearchPid} // Disable if no search is active
            >
              Clear
            </Button>
          </Box>
        </Box>
      </div>

      <div style={styles.tableContainer}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <div style={styles.tableWrapper}>
            <Table size="small" stickyHeader style={styles.table}>
              <TableHead>
                <TableRow>
                  {getColumns().map((column) => (
                    <TableCell
                      key={column.id}
                      style={{ ...styles.headerCell, width: column.width }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(data.results) &&
                  data.results.map((row, index) => (
                    <TableRow key={row.noticePropertyDataID || index}>
                      {getColumns().map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell key={column.id} style={styles.tableCell}>
                            {column.id === 'pID'
                              ? renderPidLink(value, row)
                              : value?.toString() || ''}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div style={styles.paginationWrapper}>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          labelDisplayedRows={() => ''}
        />
      </div>
    </div>
  );
};

export default PropertyDetailsView;
